import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Coins, Gem, RefreshCw } from "lucide-react";

interface DailyRewardDialogProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (data: any) => void;
  readonly initialData?: any;
  readonly mode: "add" | "edit";
  readonly isLoading?: boolean;
  readonly shopItems?: any[];
}

export function DailyRewardDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
  isLoading,
  shopItems = [],
}: DailyRewardDialogProps) {
  const [formData, setFormData] = useState(
    initialData ?? {
      day: 1,
      amount: 50,
      currencyType: "regular",
      description: "Daily login reward",
      imageUrl: "",
      isActive: true,
      bonusItemId: null as number | null,
    }
  );

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "Add New Daily Reward" : "Edit Daily Reward"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Enter the details for the new daily reward."
              : "Update the details for this daily reward."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="day">Day</Label>
                <Input
                  id="day"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.day}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      day: parseInt(e.target.value) || 1,
                    })
                  }
                  placeholder="Enter day number"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Reward Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      amount: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter reward amount"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter reward description"
                required
              />
            </div>

            <div className="space-y-2">
              <ImageUploadField
                id="imageUrl"
                label="Image URL (Optional)"
                value={formData.imageUrl ?? ""}
                onChange={(value) =>
                  setFormData({ ...formData, imageUrl: value })
                }
                placeholder="Enter image URL or upload an image"
                uploadEndpoint="/api/manager/upload-image"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currencyType">Currency Type</Label>
                <Select
                  value={formData.currencyType}
                  onValueChange={(value: "regular" | "premium") =>
                    setFormData({ ...formData, currencyType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">
                      <div className="flex items-center">
                        <Coins className="w-4 h-4 mr-2 text-yellow-500" />
                        Regular (Coins)
                      </div>
                    </SelectItem>
                    <SelectItem value="premium">
                      <div className="flex items-center">
                        <Gem className="w-4 h-4 mr-2 text-purple-500" />
                        Premium (Gems)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bonusItemId">Bonus Item (Optional)</Label>
                <Select
                  value={formData.bonusItemId?.toString() ?? "null"}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      bonusItemId: value === "null" ? null : parseInt(value),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select bonus item" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">None</SelectItem>
                    {shopItems.map((item) => (
                      <SelectItem key={item.id} value={item.id.toString()}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked })
                }
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="ml-2">
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {mode === "add" ? "Adding..." : "Updating..."}
                </>
              ) : (
                <>{mode === "add" ? "Add Reward" : "Update Reward"}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
